// utils/momentPieces.js — Anamoria SPA
// v0.5 -- Option B: expandPrivateEntries surfaces a PRIVATE piece inside a SHARED moment container
//   on the Private tab (it was in neither Shared nor Private and vanished). Private moment
//   containers and standalone private memories are unchanged.
// v0.4 — NEW-105 (2026-09-26): expandSharedEntries/expandFavoriteEntries carry the moment dayText onto each expanded piece (shown on shared-piece card footers).
// v0.3 — NEW-3: expandSharedEntries now excludes leader-only contributor pieces
//   (creatorType 'contributor' + visibilityPref 'owner_contributor_only') from the Shared tab when
//   group_spaces_enabled is on — they belong in the "Just with you" subtab. Flag off = unchanged.
// v0.2 — Memory View by type: spotlight + Text / Video / Audio / Photo
//   (July 30, 2026). Executes the reviewed mockup (momentdetail-mockup v0.9).
//   - Spotlight = the first video if any, else the first photo, else none.
//     Audio is never the spotlight; it renders in its own section.
//   - text: the note (firstTextPiece). audios: every voice piece (inline
//     VoiceCards). videoThumbs / photoThumbs: the remaining videos / photos
//     as thumbnail rails that open the lightbox. All groups keep piece_order.
//   - `photos` is the full ordered photo set (the lightbox carousel); the
//     spotlight photo is photos[0] when the spotlight is a photo.
import { firstTextPiece } from './momentTitle';

const cat = (p) => (p?.category || '').toLowerCase();

export function partitionMomentPieces(pieces = []) {
  const videos = pieces.filter((p) => cat(p) === 'video');
  const photos = pieces.filter((p) => cat(p) === 'photo');
  const audios = pieces.filter((p) => cat(p) === 'voice');
  const text = firstTextPiece(pieces) || null;

  let spotlight = null;
  if (videos.length) spotlight = { kind: 'video', piece: videos[0] };
  else if (photos.length) spotlight = { kind: 'photo', piece: photos[0] };

  const videoThumbs = spotlight && spotlight.kind === 'video' ? videos.slice(1) : videos;
  const photoThumbs = spotlight && spotlight.kind === 'photo' ? photos.slice(1) : photos;

  return { text, spotlight, videos, photos, audios, videoThumbs, photoThumbs };
}

// v0.3: Favorites tab (Option A). Expand feed entries into the favorited PIECES
// to show as cards: standalone favorited memories pass through unchanged; a
// moment entry yields each of its favorited pieces, tagged with `momentId` so
// the card can open its parent moment. Order preserved (moment order, then
// piece order). Non-favorited pieces and empty moments contribute nothing.
export function expandFavoriteEntries(entries = []) {
  const out = [];
  for (const m of entries) {
    if (m.entryType === 'moment') {
      for (const p of (m.pieces || [])) {
        if (p.isFavorite) out.push({ ...p, entryType: 'memory', momentId: m.id, dayText: m.dayText });
      }
    } else if (m.isFavorite) {
      out.push(m);
    }
  }
  return out;
}

// v0.4: Shared tab — parallel to expandFavoriteEntries (Option A). Expand feed
// entries into the SHARED (non-private) PIECES to show as cards: a moment yields
// each of its shared pieces, tagged with `momentId` so the card opens its parent
// moment; a standalone shared memory passes through (respecting the group-spaces
// review state when enabled — items awaiting review or declined are excluded, as
// the container filter does). Private pieces and empty moments contribute
// nothing. This is what surfaces an individually-shared moment piece in the
// Shared tab as if it were a standalone shared memory.
// NEW-3 (v0.3): a leader-only contributor piece (creatorType 'contributor' +
// visibilityPref 'owner_contributor_only') is NOT shared to the group — it must NOT appear on
// the Shared tab. It surfaces in the "Just with you" subtab instead. Excluded only when the
// group-spaces flag is on (flag off = byte-identical legacy behaviour).
const isLeaderOnlyContribPiece = (x, groupSpacesEnabled) =>
  groupSpacesEnabled && x.creatorType === 'contributor' && x.visibilityPref === 'owner_contributor_only';

export function expandSharedEntries(entries = [], { groupSpacesEnabled = false } = {}) {
  const out = [];
  for (const m of entries) {
    if (m.entryType === 'moment') {
      for (const p of (m.pieces || [])) {
        if (!p.isPrivate && !isLeaderOnlyContribPiece(p, groupSpacesEnabled)) out.push({ ...p, entryType: 'memory', momentId: m.id, dayText: m.dayText });
      }
    } else if (!m.isPrivate) {
      if (groupSpacesEnabled && (m.reviewStatus === 'pending' || m.reviewStatus === 'rejected')) continue;
      if (isLeaderOnlyContribPiece(m, groupSpacesEnabled)) continue;
      out.push(m);
    }
  }
  return out;
}

// v0.5: Private tab (Option B). Surface a PRIVATE piece that lives inside a SHARED moment
// container (otherwise invisible: Shared excludes it as private, and the Private container filter
// excludes it because the moment is not private). A private moment container and a standalone
// private memory pass through UNCHANGED. Mirrors expandSharedEntries (same leader-only-contrib guard).
export function expandPrivateEntries(entries = [], { groupSpacesEnabled = false } = {}) {
  const out = [];
  for (const m of entries) {
    if (m.entryType === 'moment') {
      if (m.isPrivate) {
        out.push(m); // private moment container -- shown as-is (unchanged)
      } else {
        for (const p of (m.pieces || [])) {
          if (p.isPrivate && !isLeaderOnlyContribPiece(p, groupSpacesEnabled)) {
            out.push({ ...p, entryType: 'memory', momentId: m.id, dayText: m.dayText });
          }
        }
      }
    } else if (m.isPrivate) {
      out.push(m);
    }
  }
  return out;
}
